import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { LoadingState } from "@/components/LoadingState/LoadingState";
import { useAuthStore } from "./authStore";
import { useSessionBootstrap } from "./useSession";

/**
 * Gate around authenticated routes. Bootstraps the session on first paint,
 * shows a full-page loader while checking, and redirects to /login if no
 * valid session is present.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const { hydrated } = useSessionBootstrap();
  const accessToken = useAuthStore((s) => s.accessToken);
  const mustChange = useAuthStore((s) => s.user?.must_change_password);
  const location = useLocation();

  if (!hydrated) {
    return <LoadingState fullScreen label="Restoring your session…" />;
  }

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (mustChange && location.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }

  return <>{children}</>;
}
